import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useTokens } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { getContrastTextColor } from '../utils/luminance';

const ctaDef: SlotGroupDefinition = {
  label: 'CTA Button',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Browse Our Spaces' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

export function TabbedCallouts() {
  const cta = useSlotGroup('tabbedcallouts.cta', ctaDef);

  const tokens = useTokens();
  const { headingColor, bodyColor, ctaHoverBg, ctaHoverText } = useMemo(() => {
    const primary = tokens?.color?.primary || '#B28C59';
    const secondary = tokens?.color?.secondary || '#3E441D';
    // Heading (and matching CTA outline) are black or white against primary.
    const onPrimary = getContrastTextColor(primary);
    return {
      headingColor: onPrimary,
      bodyColor: onPrimary,
      ctaHoverBg: secondary,
      ctaHoverText: getContrastTextColor(secondary),
    };
  }, [tokens]);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-[var(--vibe-color-primary)]"
      style={{
        ['--tabbed-heading' as string]: headingColor,
        ['--tabbed-body' as string]: bodyColor,
        ['--tabbed-cta-hover-bg' as string]: ctaHoverBg,
        ['--tabbed-cta-hover-text' as string]: ctaHoverText,
      }}
    >
      <div className="mx-auto flex max-w-3xl flex-col items-center px-6 py-20 text-center sm:px-8 md:py-28 lg:py-32">
        <RichTextContent
          path="tabbedcallouts.heading"
          label="Heading"
          defaultValue="<p>Apartment Living, Reimagined</p>"
          as="h2"
          className="tabbed-callout-heading whitespace-nowrap text-[1.75rem] font-normal leading-[1.2] text-[var(--tabbed-heading)] sm:text-[1.9rem] md:text-[2.25rem]"
        />

        <RichTextContent
          path="tabbedcallouts.body"
          label="Body Text"
          defaultValue='<p>Luxury, comfort, and quality at its finest. <span data-type="field-token" data-field="property_name"></span> has the latest technology built into every apartment and amenity. Make <span data-type="field-token" data-field="property_name"></span> your home and be the first to live in these beautiful new apartments.</p>'
          as="div"
          className="mt-6 max-w-2xl font-body text-base font-light leading-relaxed text-[var(--tabbed-body)] sm:text-lg md:mt-8"
        />

        <EditableSlotGroup groupPath="tabbedcallouts.cta" definition={ctaDef} className="mt-10 md:mt-12">
          <a
            href={cta.url || '/floor-plans'}
            target={cta._target || '_self'}
            className="tabbed-callout-cta inline-flex items-center justify-center px-8 py-3.5 font-body text-xs uppercase tracking-[0.2em] transition-colors duration-300 sm:text-sm"
          >
            {cta.label || 'Browse Our Spaces'}
          </a>
        </EditableSlotGroup>
      </div>

      <style>{`
        .tabbed-callout-heading {
          font-family: Georgia, 'Times New Roman', serif;
        }
        .tabbed-callout-cta {
          background: transparent;
          border: 1px solid var(--tabbed-heading);
          border-radius: 0;
          color: var(--tabbed-heading);
        }
        .tabbed-callout-cta:hover,
        .tabbed-callout-cta:active {
          background: var(--tabbed-cta-hover-bg);
          border-color: var(--tabbed-cta-hover-bg);
          color: var(--tabbed-cta-hover-text);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'TabbedCallouts',
  displayName: 'Living Callout',
  tokens: {
    'tabbedcallouts.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Apartment Living, Reimagined</p>',
    },
    'tabbedcallouts.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultValue:
        '<p>Luxury, comfort, and quality at its finest. <span data-type="field-token" data-field="property_name"></span> has the latest technology built into every apartment and amenity. Make <span data-type="field-token" data-field="property_name"></span> your home and be the first to live in these beautiful new apartments.</p>',
    },
  },
  slotGroups: {
    'tabbedcallouts.cta': ctaDef,
  },
};

(TabbedCallouts as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default TabbedCallouts;
